import { useEffect, useState } from "react";

type BreakpointMode = "min" | "max";

/**
 * Hook to detect whether the current viewport matches a given breakpoint rule.
 * Example:
 *   useIsBreakpoint("max", 768)   // true when width < 768
 *   useIsBreakpoint("min", 1024)  // true when width >= 1024
 */
export function useIsBreakpoint(mode: BreakpointMode = "max", breakpoint = 768) {
  const [matches, setMatches] = useState<boolean | undefined>(undefined);

  useEffect(() => {
    // Media queries use em (not rem) — em is universally supported and respects the
    // user's browser font-size preference. 1em = 16px at default font size.
    const minEm = breakpoint / 16;
    const maxEm = (breakpoint - 1) / 16;
    const query = mode === "min" ? `(min-width: ${minEm}em)` : `(max-width: ${maxEm}em)`;

    const mql = window.matchMedia(query);
    const onChange = (e: MediaQueryListEvent) => setMatches(e.matches);

    // Set initial value
    setMatches(mql.matches);

    // Add listener
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, [mode, breakpoint]);

  return !!matches;
}
